/**
 * NameGate — "One more thing": asks a signed-in user with no display name for
 * one, before they reach the app (vendor#1436).
 *
 * Shell: (public) frame, rendered by `RequireAuth` in place of its children
 * while `session.displayNameMissing` is true. Never `(kiosk)`.
 *
 * A member needs a display name to hold a seat — the database now refuses an
 * active seat with a blank one, and refuses clearing the name of a seated
 * member. A password sign-up supplies a name; the people who arrive without one
 * are Google/OIDC sign-ins whose provider sent none, and admin-created users.
 * Without this gate they would meet the refusal as a failed invite-accept or a
 * failed edit, so they are asked first, once.
 *
 * No design covers this screen. It reuses `AuthFrame` (one new panel line) and
 * the single-field form the invite name step already draws — the simplest
 * existing pattern — rather than inventing a layout. A quiet "Sign out" stays
 * available so the gate is never a trap.
 *
 * The write is `users_self_update`. If the server still refuses it with
 * `display_name_required` (23514) the requirement is shown inline; any other
 * failure says to try again.
 */

import { useState } from 'react';
import { Button, Stack, Text, TextInput, Title } from '@mantine/core';
import { supabase } from '../../../lib/supabase';
import { useSession } from '../../../lib/store';
import { AuthFrame } from './AuthFrame';

/** What a refusal for a blank name says. Also the profile-edit wording. */
export const NAME_REQUIRED_MESSAGE = 'A name is required — it is how your partners see you.';

export function NameGate() {
  const userId = useSession((s) => s.userId);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!userId) return;
    if (trimmed === '') {
      setError(NAME_REQUIRED_MESSAGE);
      return;
    }
    setSaving(true);
    setError(null);
    const { error: writeError } = await supabase
      .from('users')
      .update({ display_name: trimmed })
      .eq('id', userId);
    setSaving(false);
    if (writeError) {
      setError(
        writeError.code === '23514' || writeError.hint === 'display_name_required'
          ? NAME_REQUIRED_MESSAGE
          : "Couldn't save your name — try again.",
      );
      return;
    }
    useSession.setState({ displayName: trimmed, displayNameMissing: false });
  };

  return (
    <AuthFrame screen="name">
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSave();
        }}
      >
        <Stack gap={16}>
          <Title order={3} fz={20} fw={500}>
            What should we call you?
          </Title>
          <Text fz={13.5} c="dimmed" style={{ textWrap: 'pretty', lineHeight: 1.55 }}>
            Partners see this name on every card you touch, every note, every split.
          </Text>
          <TextInput
            label="Your name"
            placeholder="Sam Rivera"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            autoComplete="name"
            error={error}
            data-testid="name-gate-input"
          />
          <Button
            type="submit"
            fullWidth
            h={44}
            loading={saving}
            disabled={name.trim() === ''}
            data-testid="name-gate-continue"
          >
            Continue
          </Button>
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => void useSession.getState().signOut()}
            data-testid="name-gate-sign-out"
          >
            Sign out
          </Button>
        </Stack>
      </form>
    </AuthFrame>
  );
}
